import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { i18n } from '@lingui/core';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';
import { useIsTouchDevice } from '../../../../hooks/useIsTouchDevice.js';
import type { UploadSlot } from '../types/index.js';
import { SortableImage } from './sortable-image.js';

interface SortableImageGridProps {
  slots: (UploadSlot & { onEditDescription?: () => void; onRotate?: () => Promise<void>; isRotating?: boolean })[];
  onDelete: (key: string) => void;
  onReorder: (indices: { oldIndex: number; newIndex: number }) => void;
  disabled?: boolean;
}

export const SortableImageGrid: React.FC<SortableImageGridProps & { onFilesDrop?: (files: FileList) => void }> = ({
  slots,
  onDelete,
  onReorder,
  disabled = false,
}) => {
  const isTouchDevice = useIsTouchDevice();
  const doneSlots = slots.filter((s) => s.status === 'done');
  const uploadingSlots = slots.filter((s) => s.status !== 'done');

  // Dropzone and dndContext settings
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const hasImages = doneSlots.length > 0 || uploadingSlots.length > 0;
  const hasMoreThanOneImage = doneSlots.length > 1;

  return (
    <div className="w-full" style={hasImages ? { minHeight: 128, position: 'relative' } : { position: 'relative' }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        accessibility={{
          restoreFocus: false,
        }}
        onDragOver={(event) => {
          setDragOverId(event.over?.id?.toString() ?? null);
        }}
        onDragEnd={(event) => {
          setDragOverId(null);
          const { active, over } = event;
          if (active.id !== over?.id) {
            const oldIndex = doneSlots.findIndex((s) => s.key === active.id);
            const newIndex = doneSlots.findIndex((s) => s.key === over?.id);
            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
              onReorder({ oldIndex, newIndex });
            }
          }
        }}
        onDragCancel={() => setDragOverId(null)}
      >
        <ul
          className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-16 w-full pointer-events-auto"
          aria-label={i18n.t({
            id: 'mobility-item-insertion.form.imageUpload.imageListAriaLabel',
            message:
              'Press space to select an image, use arrow keys to move it, then press space again to drop it in the new position',
            comment:
              'Aria label for the image grid that explains the complete keyboard interaction flow: select, move, drop',
          })}
        >
          <SortableContext items={doneSlots.map((s, idx) => s.key ?? `done-${idx}`)} strategy={rectSortingStrategy}>
            {doneSlots.map((slot, idx) => (
              <li
                key={slot.key ?? `done-${idx}`}
                className="relative group focus-within:z-[6]"
                aria-label={`Image ${idx + 1} of ${doneSlots.length}`}
              >
                <SortableImage
                  slot={slot}
                  onDelete={disabled ? () => {} : onDelete}
                  isDraggable={!disabled}
                  isDragOver={dragOverId === slot.key}
                  onEditDescription={disabled ? undefined : slot.onEditDescription}
                  testid={`image-${idx}`}
                  disabled={disabled}
                  imageIndex={idx}
                />
              </li>
            ))}
            {uploadingSlots.map((slot, idx) => (
              <SortableImage
                key={slot.key ?? `uploading-${idx}`}
                slot={slot}
                onDelete={disabled ? () => {} : onDelete}
                isDraggable={false}
                isDragOver={dragOverId === slot.key}
                disabled={disabled}
                imageIndex={doneSlots.length + idx}
              />
            ))}
          </SortableContext>
        </ul>
        {hasMoreThanOneImage && (
          <p className="text-s mb-0 mt-24">
            {isTouchDevice ? (
              <Trans
                id="mobility-item-insertion.form.imageUpload.reorderInstructionsTouch"
                comment="Text shown when images has been uploaded and user can reorder them using touch"
              >
                Hold an image briefly and drag to change order of photos
              </Trans>
            ) : (
              <Trans
                id="mobility-item-insertion.form.imageUpload.reorderInstructionsClick"
                comment="Text shown when images has been uploaded and user can reorder them using mouse"
              >
                Click and drag to change order of photos
              </Trans>
            )}
          </p>
        )}
      </DndContext>
    </div>
  );
};
